import MultipleInput from "../elements/MultipleInput";
import { useEffect, useState } from "react";
import { BASE_URL, CROP_LIST, DEFAULT_DATA_ITEM, DEFAULT_DATA_ITEM_ARRAY, TypeDataItem, TypeDataSource, TypeValueChain } from "../../helpers/constants";
import useApiRequest from "../../helpers/api_request";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectAccessToken } from "../../features/auth/authSlice";
import AppButton from "../elements/AppButton";
import { useNavigate, useParams } from "react-router-dom";
import { showToastMessage } from "../../features/generalSlice";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import useSellingData from "../../hooks/useSellingData";
import AppSpinner from "../elements/AppSpinner";

export type TypeSellingData = {
    "value_chain_id": string,
    "year": string,
    "volume_exported": TypeDataItem,
    "registered_exporters": TypeDataItem,
    "no_of_exporters_per_year": TypeDataItem,
    "no_of_importers_per_year": TypeDataItem,
    "monitory_value_exports": TypeDataItem,
    "monitory_value_imported_products": TypeDataItem,
    "monitory_value_of_domestic_production": TypeDataItem,
    "avg_export_price": TypeDataItem,
    "list_of_grades_exported": TypeDataItem,
    "no_of_financial_service_providers": TypeDataItem,
    "no_of_bds_providers": TypeDataItem,
    "list_of_bds_providers": TypeDataItem,
    "created_at"?: string,
    "value_chain"?: string,
    "_id"?: string
}

const CreateSellingPage = () => {
    const [dataSources, setDataSources] = useState<TypeDataSource[] | []>([]);
    const apiRequest = useApiRequest();
    const accessToken = useAppSelector(selectAccessToken);
    const [valueChains, setValueChains] = useState<TypeValueChain[] | []>([]);
    const [isSavingData, setIsSavingData] = useState(false);
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const [sellingData, setSellingData] = useState<TypeSellingData>({
        "value_chain_id": "",
        "value_chain": "",
        "year": "",
        "volume_exported": DEFAULT_DATA_ITEM,
        "registered_exporters": DEFAULT_DATA_ITEM,
        "no_of_exporters_per_year": DEFAULT_DATA_ITEM,
        "no_of_importers_per_year": DEFAULT_DATA_ITEM,
        "monitory_value_exports": DEFAULT_DATA_ITEM,
        "monitory_value_imported_products": DEFAULT_DATA_ITEM,
        "monitory_value_of_domestic_production": DEFAULT_DATA_ITEM,
        "avg_export_price": DEFAULT_DATA_ITEM,
        "list_of_grades_exported": DEFAULT_DATA_ITEM_ARRAY,
        "no_of_financial_service_providers": DEFAULT_DATA_ITEM,
        "no_of_bds_providers": DEFAULT_DATA_ITEM,
        "list_of_bds_providers": DEFAULT_DATA_ITEM_ARRAY
    });
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 6 }, (_, i) => currentYear - i);
    const { id } = useParams();

    const { loadingData, sellingData: sellingDataList, getSellingData } = useSellingData();

    useEffect(() => {
        if (id != undefined) {
            getSellingData();
        }
    }, [id]);


    useEffect(() => {
        if (id != undefined) {
            if (sellingDataList.length > 0) {
                const record = sellingDataList.filter((item) => item._id == id)[0]
                for (const key in record) {
                    const field = record[key as keyof TypeSellingData]
                    setSellingData((prevData) => ({
                        ...prevData,
                        [key]: field,
                    }));
                }
                addToDataSources(record)
            }
        }
    }, [sellingDataList])


    const addToDataSources = (record: TypeSellingData) => {
        const otherDataSources: TypeDataSource[] = []
        for (const key in record) {
            const field = record[key as keyof TypeSellingData]
            if (typeof field == "object") {
                if (field) {
                    const dataSource = {
                        id: null,
                        name: field.data_source
                    }
                    otherDataSources.push(dataSource)
                }
            }
        }
        const uniqueValues = otherDataSources.filter(
            (item, index, self) =>
                index === self.findIndex((i) => i.name === item.name)
        );
        setDataSources((prev) => [...prev, ...uniqueValues])
    }

    useEffect(() => {
        getDataSources()
        getValueChains()
    }, [])

    const saveSellingData = async () => {
        if (sellingData.value_chain === "") {
            dispatch(showToastMessage({
                message: "Value chain is required",
                type: "error",
                position: toast.POSITION.TOP_RIGHT
            }));
            return
        }

        setIsSavingData(true)
        await apiRequest({
            method: 'post',
            url: BASE_URL + "/api/marketing/create",
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            data: sellingData
        }).then((response) => {
            setIsSavingData(false)
            dispatch(showToastMessage({
                message: "Marketing data Saved",
                type: "success",
                position: toast.POSITION.TOP_RIGHT
            }));
            navigate("/selling")
        }).catch((error) => {
            const errorResponse = error as AxiosError;
            if (errorResponse.response) {
                const data = errorResponse.response.data as { message: string, status: string };
                dispatch(showToastMessage({
                    message: data.message,
                    type: "error",
                    position: toast.POSITION.TOP_RIGHT
                }));
            } else {
                dispatch(showToastMessage({
                    message: "Something went wrong",
                    type: "error",
                    position: toast.POSITION.TOP_RIGHT
                }));
            }
            setIsSavingData(false)
        });
    }


    const getValueChains = async () => {
        await apiRequest({
            method: 'get',
            url: BASE_URL + "/api/value-chain",
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }).then((response) => {
            setValueChains(response.data)
        }).catch((error) => {

        });
    }


    const getDataSources = async () => {
        await apiRequest({
            method: 'get',
            url: BASE_URL + "/api/data-source",
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }).then((response) => {
            setDataSources((prev) => [...prev, ...response.data])

        }).catch((error) => {

        });
    }

    const setDataItemValue = (field: keyof TypeSellingData, value: string | string[]) => {
        setSellingData({
            ...sellingData,
            [field]: {
                ...sellingData[field] as TypeDataItem, value: value
            }
        });
    }

    const setDataItemSource = (field: keyof TypeSellingData, data_source: string | string[]) => {
        setSellingData({
            ...sellingData,
            [field]: {
                ...sellingData[field] as TypeDataItem, data_source: data_source
            }
        });
    }

    return (
        <div>
            <div className="card">
                <div className=" card-body min-h-half-screen">
                    <div className="flex">
                        <nav className="flex justify-between " aria-label="Breadcrumb">
                            <ol className="inline-flex items-center mb-3 sm:mb-0">
                                <li>
                                    <div className="flex items-center">
                                        <h5 className=" card-title ">Marketing & Selling</h5>
                                    </div>
                                </li>
                                <span className="mx-2 text-gray-400">/</span>
                                <li aria-current="page">
                                    <div className="flex items-center">
                                        <h5 className=" card-title ">{id ? "Edit" : "Create"}</h5>
                                    </div>
                                </li>
                            </ol>
                        </nav>
                    </div>
                    {(loadingData) && <AppSpinner TopClass=" flex pt-0" />}

                    <div className=" h-fit flex-grow flex flex-col justify-center ">
                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">Value Chain</label>
                            <select value={sellingData.value_chain} onChange={(e) => setSellingData({ ...sellingData, value_chain: e.target.value })} className="form-input-2" name="" id="">
                                <option value="">--Select--</option>
                                {valueChains.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                            </select>
                        </div>

                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">Year</label>
                            <select value={sellingData.year} onChange={(e) => setSellingData({ ...sellingData, year: e.target.value })} className="form-input-2" name="" id="">
                                <option value="">Select</option>
                                {years.map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>
                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">Volume exported (Kgs)</label>

                            <div className="grid grid-cols-2 gap-2">
                                <input value={sellingData.volume_exported.value} onChange={(e) => setDataItemValue('volume_exported', e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <div>
                                    <select value={sellingData.volume_exported.data_source} onChange={(e) => setDataItemSource('volume_exported', e.target.value)} className="form-input-2" name="" id="">
                                        <option value="">--Select Source--</option>
                                        {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">No Export licenses issued/Registered Exporters</label>

                            <div className="grid grid-cols-2 gap-2">
                                <input value={sellingData.registered_exporters.value} onChange={(e) => setDataItemValue('registered_exporters', e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <div>
                                    <select value={sellingData.registered_exporters.data_source} onChange={(e) => setDataItemSource('registered_exporters', e.target.value)} className="form-input-2" name="" id="">
                                        <option value="">--Select Source--</option>
                                        {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">No of exporters per year.</label>

                            <div className="grid grid-cols-2 gap-2">
                                <input value={sellingData.no_of_exporters_per_year.value} onChange={(e) => setDataItemValue('no_of_exporters_per_year', e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <div>
                                    <select value={sellingData.no_of_exporters_per_year.data_source} onChange={(e) => setDataItemSource('no_of_exporters_per_year', e.target.value)} className="form-input-2" name="" id="">
                                        <option value="">--Select Source--</option>
                                        {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">  Monitory value exports (Ugx) </label>

                            <div className="grid grid-cols-2 gap-2">
                                <input value={sellingData.monitory_value_exports.value} onChange={(e) => setDataItemValue('monitory_value_exports', e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <div>
                                    <select value={sellingData.monitory_value_exports.data_source} onChange={(e) => setDataItemSource('monitory_value_exports', e.target.value)} className="form-input-2" name="" id="">
                                        <option value="">--Select Source--</option>
                                        {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className=" w-2/3 mt-4 ">
                            <label className="block text-sm font-medium text-gray-700 "> Monitory value of imported products  .</label>

                            <div className="grid grid-cols-2 gap-2">
                                <input value={sellingData.monitory_value_imported_products.value} onChange={(e) => setDataItemValue('monitory_value_imported_products', e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <div>
                                    <select value={sellingData.monitory_value_imported_products.data_source} onChange={(e) => setDataItemSource('monitory_value_imported_products', e.target.value)} className="form-input-2" name="" id="">
                                        <option value="">--Select Source--</option>
                                        {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">Monitory value of domestic consumption</label>

                            <div className="grid grid-cols-2 gap-2">
                                <input value={sellingData.monitory_value_of_domestic_production.value} onChange={(e) => setDataItemValue('monitory_value_of_domestic_production', e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <div>
                                    <select value={sellingData.monitory_value_of_domestic_production.data_source} onChange={(e) => setDataItemSource('monitory_value_of_domestic_production', e.target.value)} className="form-input-2" name="" id="">
                                        <option value="">--Select Source--</option>
                                        {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">No of importers per year</label>
                            <div className="grid grid-cols-2 gap-2">
                                <input value={sellingData.no_of_importers_per_year.value} onChange={(e) => setDataItemValue('no_of_importers_per_year', e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <div>
                                    <select value={sellingData.no_of_importers_per_year.data_source} onChange={(e) => setDataItemSource('no_of_importers_per_year', e.target.value)} className="form-input-2" name="" id="">
                                        <option value="">--Select Source--</option>
                                        {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">Avg export price per kg</label>

                            <div className="grid grid-cols-2 gap-2">
                                <input value={sellingData.avg_export_price.value} onChange={(e) => setDataItemValue('avg_export_price', e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <div>
                                    <select value={sellingData.avg_export_price.data_source} onChange={(e) => setDataItemSource('avg_export_price', e.target.value)} className="form-input-2" name="" id="">
                                        <option value="">--Select Source--</option>
                                        {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">List of grades exported </label>
                            <div className="grid grid-cols-2 gap-2">
                                <MultipleInput inputValues={sellingData.list_of_grades_exported.value as string[]} getValues={(values) => setDataItemValue('list_of_grades_exported', values)} />
                                <div>
                                    <select value={sellingData.list_of_grades_exported.data_source} onChange={(e) => setDataItemSource('list_of_grades_exported', e.target.value)} className="form-input-2" name="" id="">
                                        <option value="">--Select Source--</option>
                                        {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">No of financial service providers </label>
                            <div className="grid grid-cols-2 gap-2">
                                <input value={sellingData.no_of_financial_service_providers.value} onChange={(e) => setDataItemValue('no_of_financial_service_providers', e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <div>
                                    <select value={sellingData.no_of_financial_service_providers.data_source} onChange={(e) => setDataItemSource('no_of_financial_service_providers', e.target.value)} className="form-input-2" name="" id="">
                                        <option value="">--Select Source--</option>
                                        {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">No of BDS providers/programs. </label>

                            <div className="grid grid-cols-2 gap-2">
                                <input value={sellingData.no_of_bds_providers.value} onChange={(e) => setDataItemValue('no_of_bds_providers', e.target.value)} type="text" id="name" className="form-input-2" placeholder="" />
                                <div>
                                    <select value={sellingData.no_of_bds_providers.data_source} onChange={(e) => setDataItemSource('no_of_bds_providers', e.target.value)} className="form-input-2" name="" id="">
                                        <option value="">--Select Source--</option>
                                        {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className=" w-2/3 mt-4">
                            <label className="block text-sm font-medium text-gray-700 ">List of BDS providers/programs. </label>
                            <div className="grid grid-cols-2 gap-2">
                                <MultipleInput inputValues={sellingData.list_of_bds_providers.value as string[]} getValues={(values) => setDataItemValue('list_of_bds_providers', values)} />
                                <div>
                                    <select value={sellingData.list_of_bds_providers.data_source} onChange={(e) => setDataItemSource('list_of_bds_providers', e.target.value)} className="form-input-2" name="" id="">
                                        <option value="">--Select Source--</option>
                                        {dataSources.map((item, key) => <option key={key} value={item.name}>{item.name}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="w-2/3 mt-4">
                            <AppButton callBackFun={() => saveSellingData()} showLoader={isSavingData} spinnerClass="inline w-3 h-3 mr-2 text-prim-yellow animate-spin fill-black" className="prim-btn-2 mb-2" text="Save" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default CreateSellingPage;